import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Heart, MapPin, Binoculars, ArrowLeft } from 'lucide-react';
import RegisterForm from "../components/loginSession/RegisterForm";
import LoginForm from "../components/loginSession/LoginForm";
import tempLogo from '../static/images/temporary_logo.png';
import loginBg from '../static/images/login-page.jpg';
import LanguageSelector from "../components/navbar/LanguageSelector";

const FEATURES = [
    { icon: Heart, key: 'save' },
    { icon: MapPin, key: 'discover' },
    { icon: Binoculars, key: 'live' },
];

const RegisterLoginPage = () => {
    const { t } = useTranslation();
    const [section, setSection] = useState('Login');

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const querySection = searchParams.get('section');
        if (querySection === 'Login' || querySection === 'Register') {
            setSection(querySection);
        }
    }, []);

    const isLogin = section === 'Login';

    return (
        <div className="fixed inset-0 z-[5] overflow-y-auto bg-[#f7f7f8]">

            {/* Selettore lingua dedicato: in alto a destra, simmetrico alla freccia indietro.
                La Navbar è nascosta su questa rotta (vedi ConditionalNavbar in App.js). */}
            <div
                className="absolute right-4 z-20 h-10 px-3 rounded-full bg-white/85 backdrop-blur shadow flex items-center"
                style={{ top: 'calc(max(env(safe-area-inset-top), 16px) + 12px)' }}
            >
                <LanguageSelector />
            </div>

            <div className="min-h-full flex flex-col md:flex-row">

                {/* HERO */}
                <div
                    className="relative w-full md:w-1/2 md:min-h-screen overflow-hidden"
                    style={{
                        backgroundImage: `url(${loginBg})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                >
                    {/* Fade verso il bg pagina in basso (mobile: si fonde col card; desktop: ammorbidisce il bordo inferiore) */}
                    <div className="absolute inset-0 bg-gradient-to-b from-white/0 via-white/0 to-[#f7f7f8]" />

                    {/* Back arrow: solo desktop, fuori dal pannello (top-left, simmetrico al
                        selettore lingua). Su mobile il "indietro" lo fornisce la BaseBar in basso. */}
                    <Link
                        to="/"
                        aria-label="back"
                        className="absolute left-4 z-20 w-10 h-10 rounded-full bg-white/85 backdrop-blur hidden md:flex items-center justify-center shadow"
                        style={{ top: 'calc(max(env(safe-area-inset-top), 16px) + 12px)' }}
                    >
                        <ArrowLeft className="w-5 h-5 text-[#225424]" />
                    </Link>

                    <div
                        className="relative z-10 flex flex-col h-full px-6 pb-4 md:px-12 md:pb-12"
                        style={{ paddingTop: 'max(env(safe-area-inset-top), 24px)' }}
                    >
                        <div className="mt-2 md:mt-20">
                            {/* Pannello frosted glass: logo + titolo (+ feature su mobile per risparmiare verticale) */}
                            <div className="max-w-md bg-white/60 rounded-2xl px-5 py-5 mx-auto md:mx-0 md:w-fit">
                                {/* Header: logo */}
                                <div className="flex items-center gap-3 mb-4">
                                    <img src={tempLogo} alt="TicinoWild" className="w-36 md:w-48" />
                                </div>
                                <h1 className="!text-2xl md:!text-4xl lg:!text-5xl !font-bold text-[#225424] leading-tight md:max-w-[14ch]">
                                    <span className="block md:inline">{t('RegisterLoginPage.hero.titleLine1')}</span>{' '}
                                    <span className="block md:inline">{t('RegisterLoginPage.hero.titleLine2')}</span>{' '}
                                    <span className="block md:inline">{t('RegisterLoginPage.hero.titleLine3')}</span>
                                </h1>
                                {/* Sottotitolo solo desktop */}
                                <p className="hidden md:block mt-3 text-sm md:text-base text-neutral-900 max-w-[34ch] leading-relaxed">
                                    {t('RegisterLoginPage.hero.subtitle')}
                                </p>
                            </div>
                        </div>

                        {/* Feature al fondo della hero: su mobile sotto il pannello, su desktop al fondo della colonna */}
                        <div className="flex mt-3 md:mt-auto pt-2 md:pt-10 items-start justify-around gap-2">
                            {FEATURES.map(({ icon: Icon, key }) => (
                                <div key={key} className="flex flex-col items-center text-center max-w-[110px]">
                                    <div className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center mb-2">
                                        <Icon className="w-6 h-6 text-[#225424]" />
                                    </div>
                                    <span className="text-sm font-semibold text-neutral-900 leading-tight">
                                        {t(`RegisterLoginPage.hero.features.${key}.title`)}
                                    </span>
                                    <span className="text-[11px] text-neutral-700 leading-tight mt-0.5">
                                        {t(`RegisterLoginPage.hero.features.${key}.subtitle`)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* FORM AREA */}
                <div className="mt-2 md:mt-0 md:w-1/2 md:min-h-screen flex items-start md:items-center justify-center px-4 pb-[calc(80px+2.5rem)] md:px-8 md:py-12 relative z-10">
                    <div className="w-full max-w-md bg-white rounded-3xl shadow-xl p-6 pt-8 md:p-8">
                        {isLogin
                            ? <LoginForm setSection={setSection} />
                            : <RegisterForm setSection={setSection} />}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default RegisterLoginPage;
